import React, { createContext, useContext, useState, useEffect } from "react";
import axios, { AxiosError } from "axios";
import { useSeason } from "./SeasonContext";
import { useClub } from "./ClubContext";
import {
  DepthChart,
  Side,
  PersonnelGroup,
  GameDepthChart,
  DepthChartPlayer,
  DepthChartPosition,
  DepthChartPlayerAssignment,
} from "../types/DepthChartTypes";
import { AllSideTabs, API_BASE_URL } from "../constants";

interface DepthChartContextType {
  handleTabChange: (newSide: Side) => Promise<void>;
  saveDepthChart: () => Promise<void>;
  deleteDepthChart: (id: number) => Promise<void>;
  fetchTemplateDepthChart: (newSide: Side) => Promise<DepthChart>;
  createNewFromTemplate: (side: Side) => Promise<void>;
  fetchDepthCharts: (
    selectedClubId: number,
    selectedSeasonId: number,
    selectedSide: Side
  ) => Promise<DepthChart[]>;
  isLoading: boolean;
  error: string | null;
  success: boolean;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  selectedDepthChart: DepthChart | null;
  setSelectedDepthChart: React.Dispatch<
    React.SetStateAction<DepthChart | null>
  >;
  selectedSide: Side;
  setSelectedSide: (side: Side) => void;
  depthCharts: DepthChart[];
  setDepthCharts: (depthCharts: DepthChart[]) => void;
  selectedPersonnelGroup: PersonnelGroup | null;
  setSelectedPersonnelGroup: (personnelGroup: PersonnelGroup | null) => void;
  selectedTab: number;
  setSelectedTab: (tab: number) => void;
  templateDepthChart: DepthChart | null;
  setTemplateDepthChart: (templateDepthChart: DepthChart | null) => void;
  addDepthChartToGame: (chartId: number, gameId: number) => Promise<void>;
  gameDepthChart: GameDepthChart | null;
  setGameDepthChart: (gameDepthChart: GameDepthChart | null) => void;
  getDepthChartsForGame: (gameId: number) => Promise<void>;
  korReturnPlayers: DepthChartPlayer[];
  ballCarriers: DepthChartPlayer[];
  receivers: DepthChartPlayer[];
}

const DepthChartContext = createContext<DepthChartContextType | undefined>(
  undefined
);

export const DepthChartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedSeason } = useSeason();
  const { club } = useClub();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDepthChart, setSelectedDepthChart] =
    useState<DepthChart | null>(null);
  const [selectedSide, setSelectedSide] = useState<Side>("OFFENSE");
  const [depthCharts, setDepthCharts] = useState<DepthChart[]>([]);
  const [templateDepthChart, setTemplateDepthChart] =
    useState<DepthChart | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedPersonnelGroup, setSelectedPersonnelGroup] =
    useState<PersonnelGroup | null>(null);

  // Game specific depth charts
  const [gameDepthChart, setGameDepthChart] = useState<GameDepthChart | null>(
    null
  );
  const [korReturnPlayers, setKorReturnPlayers] = useState<DepthChartPlayer[]>(
    []
  );
  const [ballCarriers, setBallCarriers] = useState<DepthChartPlayer[]>([]);
  const [receivers, setReceivers] = useState<DepthChartPlayer[]>([]);

  // Load depth charts for the given season and club
  useEffect(() => {
    if (selectedSeason && club) {
      fetchDepthCharts(club.id, selectedSeason.id, selectedSide);
      fetchTemplateDepthChart(selectedSide);
    }
  }, [selectedSeason, club]);

  const fetchTemplateDepthChart = async (
    newSide: Side
  ): Promise<DepthChart> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/depth-charts/get_template/`,
        {
          params: { side: newSide, season_id: selectedSeason?.id },
        }
      );
      setSelectedDepthChart(response.data);
      setTemplateDepthChart(response.data);
      return response.data;
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching template depth chart");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const saveDepthChart = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    if (!selectedDepthChart) {
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/depth-charts/`, {
        name: selectedDepthChart.name,
        side: selectedDepthChart.side,
        personnel_group: selectedDepthChart.personnel_group,
        game: selectedDepthChart.game,
        positions: selectedDepthChart.positions,
        season: selectedSeason?.id,
        club: club?.id,
        is_template: selectedDepthChart.is_template,
      });
      if (selectedDepthChart.is_template) {
        setTemplateDepthChart(selectedDepthChart);
      } else {
        const newDepthCharts = depthCharts.filter(
          (depthChart) => depthChart.id !== selectedDepthChart.id
        );
        setDepthCharts([...newDepthCharts, selectedDepthChart]);
      }
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error saving depth chart");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepthCharts = async (
    selectedClubId: number,
    selectedSeasonId: number,
    selectedSide: Side
  ): Promise<DepthChart[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get<DepthChart[]>(
        `${API_BASE_URL}/depth-charts/`,
        {
          params: {
            club: selectedClubId,
            season: selectedSeasonId,
            side: selectedSide,
            is_template: false,
          },
        }
      );
      setDepthCharts(response.data);
      return response.data;
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching depth charts");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDepthChart = async (id: number): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/depth-charts/${id}/`);
      setDepthCharts(depthCharts.filter((depthChart) => depthChart.id !== id));
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error deleting depth chart");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = async (newSide: Side) => {
    setIsLoading(true);
    try {
      setSelectedTab(AllSideTabs.findIndex((tab) => tab.value === newSide));
      setSelectedSide(newSide);
      setSelectedDepthChart(null);
      setEditMode(false);

      if (selectedSeason && club) {
        await Promise.all([
          fetchTemplateDepthChart(newSide),
          fetchDepthCharts(club.id, selectedSeason.id, newSide),
        ]);
      }
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching depth charts");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const createNewFromTemplate = async (side: Side) => {
    setIsLoading(true);
    setError(null);
    try {
      await fetchTemplateDepthChart(side);

      if (!templateDepthChart) {
        throw new Error("Failed to load template depth chart");
      }

      // Create a new chart based on the template
      const newChart: DepthChart = {
        ...templateDepthChart,
        id: undefined, // This will be set by the backend
        name: `New ${side} Chart`,
        is_template: false,
        created_at: new Date().toISOString(),
      };

      // Set this as the selected chart
      setSelectedDepthChart(newChart);
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error creating new chart from template");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const addDepthChartToGame = async (chartId: number, gameId: number) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/depth-charts/add_to_game/`,
        {
          depth_chart_id: chartId,
          game_id: gameId,
        }
      );
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error adding depth chart to game");
      throw err;
    }
  };

  const getDepthChartsForGame = async (gameId: number) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/depth-charts/for_game/`,
        {
          params: { game_id: gameId },
        }
      );

      setGameDepthChart(response.data);

      const korReturnPlayers: DepthChartPlayer[] = [];
      response.data.KICKOFF_RETURN?.forEach((chart: DepthChart) => {
        chart.positions.forEach((position: DepthChartPosition) => {
          if (
            position.position_name === "R" &&
            position?.player_assignments?.[0]?.depth_chart_player
          ) {
            korReturnPlayers.push(
              position?.player_assignments?.[0]?.depth_chart_player
            );
          }
          if (
            position.position_name === "OR" &&
            position?.player_assignments?.[0]?.depth_chart_player
          ) {
            korReturnPlayers.push(
              position?.player_assignments?.[0]?.depth_chart_player
            );
          }
        });
      });
      setKorReturnPlayers(korReturnPlayers);

      const ballCarriers: DepthChartPlayer[] = [];
      response.data.OFFENSE?.forEach((chart: DepthChart) => {
        chart.positions.forEach((position: DepthChartPosition) => {
          position.player_assignments?.forEach(
            (pa: DepthChartPlayerAssignment) => {
              if (pa.depth_chart_player.player?.offensive_position === "RB") {
                ballCarriers.push(pa.depth_chart_player);
              }
              if (
                pa.depth_chart_player.player?.offensive_position === "QB" &&
                pa.depth_order === 1
              ) {
                ballCarriers.push(pa.depth_chart_player);
              }
            }
          );
        });
      });
      setBallCarriers(ballCarriers);

      const receivers: DepthChartPlayer[] = [];
      response.data.OFFENSE?.forEach((chart: DepthChart) => {
        chart.positions.forEach((position: DepthChartPosition) => {
          position.player_assignments?.forEach(
            (pa: DepthChartPlayerAssignment) => {
              if (
                pa.depth_chart_player.player?.offensive_position === "WR" &&
                pa.depth_order < 3
              ) {
                receivers.push(pa.depth_chart_player);
              }
              if (
                pa.depth_chart_player.player?.offensive_position === "TE" &&
                pa.depth_order < 3
              ) {
                receivers.push(pa.depth_chart_player);
              }
            }
          );
        });
      });
      setReceivers(receivers);
    } catch (err: unknown) {
      const error = err as AxiosError;
      setError(error.message || "Error fetching depth charts for game");
      throw err;
    }
  };

  return (
    <DepthChartContext.Provider
      value={{
        isLoading,
        error,
        success,
        editMode,
        selectedDepthChart,
        selectedSide,
        depthCharts,
        selectedPersonnelGroup,
        selectedTab,
        templateDepthChart,
        gameDepthChart,
        korReturnPlayers,
        ballCarriers,
        receivers,
        saveDepthChart,
        fetchDepthCharts,
        deleteDepthChart,
        setEditMode,
        setSelectedDepthChart,
        setSelectedSide,
        setDepthCharts,
        setSelectedPersonnelGroup,
        fetchTemplateDepthChart,
        createNewFromTemplate,
        handleTabChange,
        setSelectedTab,
        setTemplateDepthChart,
        addDepthChartToGame,
        setGameDepthChart,
        getDepthChartsForGame,
      }}
    >
      {children}
    </DepthChartContext.Provider>
  );
};

export const useDepthChart = () => {
  const context = useContext(DepthChartContext);
  if (context === undefined) {
    throw new Error("useDepthChart must be used within a DepthChartProvider");
  }
  return context;
};
